import { useMemo } from 'react';
import { useShallow } from 'zustand/shallow';
import { useLayoutStore } from './layoutStoreBase';
import { dedupeTabsById } from '../utils/layoutTabUtils';
import {
  buildTabPageScrollKey,
  type EndpointTab,
  type Tab,
  type WebPageTab,
} from './layoutStoreTypes';

// Convenience hooks for specific parts of the state
export const useSidebarState = () => {
  const selectedTab = useLayoutStore((state) => state.selectedSidebarTab);
  const setSidebarTab = useLayoutStore((state) => state.setSidebarTab);

  return { selectedTab, setSidebarTab };
};

export const useMainContentTabs = () => {
  const {
    tabs,
    activeTabId,
    tabNavigationByTabId,
    globalNavigation,
    openTab,
    navigateInActiveTab,
    openInNewTab,
    openOrFocusTab,
    goBackInActiveTab,
    goForwardInActiveTab,
    goBackInGlobalHistory,
    goForwardInGlobalHistory,
    updateTab,
    closeTab,
    setActiveTab,
    togglePinTab,
    reorderTabs,
    closeTargetSpecificTabs,
  } = useLayoutStore(
    useShallow((state) => ({
      tabs: state.mainContentTabs,
      activeTabId: state.activeMainContentTabId,
      tabNavigationByTabId: state.tabNavigationByTabId,
      globalNavigation: state.globalNavigation,
      openTab: state.openMainContentTab,
      navigateInActiveTab: state.navigateInActiveMainContentTab,
      openInNewTab: state.openMainContentTabInNewInstance,
      openOrFocusTab: state.openOrFocusMainContentTab,
      goBackInActiveTab: state.goBackInActiveMainContentTab,
      goForwardInActiveTab: state.goForwardInActiveMainContentTab,
      goBackInGlobalHistory: state.goBackInGlobalHistory,
      goForwardInGlobalHistory: state.goForwardInGlobalHistory,
      updateTab: state.updateMainContentTab,
      closeTab: state.closeMainContentTab,
      setActiveTab: state.setActiveMainContentTab,
      togglePinTab: state.togglePinTab,
      reorderTabs: state.reorderMainContentTabs,
      closeTargetSpecificTabs: state.closeTargetSpecificTabs,
    })),
  );

  const uniqueTabs = useMemo(() => dedupeTabsById({ tabs }), [tabs]);

  const tabsWithActiveState = useMemo(
    () =>
      uniqueTabs.map((tab) => ({
        ...tab,
        isActive: tab.id === activeTabId,
      })),
    [activeTabId, uniqueTabs],
  );

  const activeTab = useMemo(
    () => tabsWithActiveState.find((tab) => tab.isActive),
    [tabsWithActiveState],
  );
  const activeTabNavigation = activeTabId
    ? tabNavigationByTabId[activeTabId]
    : undefined;
  const canGoBackInActiveTab = Boolean(
    activeTabNavigation && activeTabNavigation.currentIndex > 0,
  );
  const canGoForwardInActiveTab = Boolean(
    activeTabNavigation &&
      activeTabNavigation.currentIndex < activeTabNavigation.entries.length - 1,
  );
  const activePageEntry =
    activeTabNavigation?.entries[activeTabNavigation.currentIndex];
  const activePageScrollKey = useMemo(
    () =>
      activeTabId && activePageEntry
        ? buildTabPageScrollKey({
            tabId: activeTabId,
            pageId: activePageEntry.pageId,
            pageIndex: activeTabNavigation.currentIndex,
          })
        : undefined,
    [activePageEntry, activeTabId, activeTabNavigation?.currentIndex],
  );
  const existingTabIds = useMemo(
    () => new Set(tabs.map((tab) => tab.id)),
    [tabs],
  );
  const cangoBackInGlobalHistory = useMemo(
    () =>
      globalNavigation.entries
        .slice(0, globalNavigation.currentIndex)
        .some((historyEntry) => existingTabIds.has(historyEntry?.tabId ?? '')),
    [existingTabIds, globalNavigation.currentIndex, globalNavigation.entries],
  );
  const cangoForwardInGlobalHistory = useMemo(
    () =>
      globalNavigation.entries
        .slice(globalNavigation.currentIndex + 1)
        .some((historyEntry) => existingTabIds.has(historyEntry?.tabId ?? '')),
    [existingTabIds, globalNavigation.currentIndex, globalNavigation.entries],
  );

  return {
    tabs: tabsWithActiveState,
    activeTab,
    activeTabId,
    openTab,
    navigateInActiveTab,
    openInNewTab,
    openOrFocusTab,
    goBackInActiveTab,
    goForwardInActiveTab,
    goBackInGlobalHistory,
    goForwardInGlobalHistory,
    canGoBackInActiveTab,
    canGoForwardInActiveTab,
    cangoBackInGlobalHistory,
    cangoForwardInGlobalHistory,
    activePageEntry,
    activePageScrollKey,
    updateTab,
    closeTab,
    setActiveTab,
    togglePinTab,
    reorderTabs,
    closeTargetSpecificTabs,
  };
};

export const usePanelVisibility = () => {
  const showSidePanels = useLayoutStore((state) => state.showSidePanels);
  const setSidePanelsVisible = useLayoutStore(
    (state) => state.setSidePanelsVisible,
  );

  return {
    showSidePanels,
    setSidePanelsVisible,
  };
};

export const usePanelCollapse = () => {
  const isDomainTreeCollapsed = useLayoutStore(
    (state) => state.isDomainTreeCollapsed,
  );
  const isChatCollapsed = useLayoutStore((state) => state.isChatCollapsed);
  const isReplayCollectionsCollapsed = useLayoutStore(
    (state) => state.isReplayCollectionsCollapsed,
  );
  const isAiSignalsKindNavCollapsed = useLayoutStore(
    (state) => state.isAiSignalsKindNavCollapsed,
  );
  const setDomainTreeCollapsed = useLayoutStore(
    (state) => state.setDomainTreeCollapsed,
  );
  const setChatCollapsed = useLayoutStore((state) => state.setChatCollapsed);
  const setReplayCollectionsCollapsed = useLayoutStore(
    (state) => state.setReplayCollectionsCollapsed,
  );
  const setAiSignalsKindNavCollapsed = useLayoutStore(
    (state) => state.setAiSignalsKindNavCollapsed,
  );
  const toggleDomainTreeCollapsed = useLayoutStore(
    (state) => state.toggleDomainTreeCollapsed,
  );
  const toggleChatCollapsed = useLayoutStore(
    (state) => state.toggleChatCollapsed,
  );
  const toggleReplayCollectionsCollapsed = useLayoutStore(
    (state) => state.toggleReplayCollectionsCollapsed,
  );
  const toggleAiSignalsKindNavCollapsed = useLayoutStore(
    (state) => state.toggleAiSignalsKindNavCollapsed,
  );

  return {
    isDomainTreeCollapsed,
    isChatCollapsed,
    isReplayCollectionsCollapsed,
    isAiSignalsKindNavCollapsed,
    setDomainTreeCollapsed,
    setChatCollapsed,
    setReplayCollectionsCollapsed,
    setAiSignalsKindNavCollapsed,
    toggleDomainTreeCollapsed,
    toggleChatCollapsed,
    toggleReplayCollectionsCollapsed,
    toggleAiSignalsKindNavCollapsed,
  };
};

export const useSidebarMinimized = () => {
  const isSidebarMinimized = useLayoutStore(
    (state) => state.isSidebarMinimized,
  );
  const setSidebarMinimized = useLayoutStore(
    (state) => state.setSidebarMinimized,
  );
  const toggleSidebarMinimized = useLayoutStore(
    (state) => state.toggleSidebarMinimized,
  );

  return {
    isSidebarMinimized,
    setSidebarMinimized,
    toggleSidebarMinimized,
  };
};

export const useRightPanelView = () => {
  const rightPanelView = useLayoutStore((state) => state.rightPanelView);
  const setRightPanelView = useLayoutStore((state) => state.setRightPanelView);

  return {
    rightPanelView,
    setRightPanelView,
  };
};

export const useMainPanelSizes = () => {
  const mainPanelSizes = useLayoutStore((state) => state.mainPanelSizes);
  const setMainPanelSizes = useLayoutStore((state) => state.setMainPanelSizes);

  return {
    mainPanelSizes,
    setMainPanelSizes,
  };
};

export const useEndpointTabs = (endpointId: string) => {
  const endpointTabs = useLayoutStore((state) => state.endpointTabs);
  const setEndpointTab = useLayoutStore((state) => state.setEndpointTab);

  const defaultTab = endpointId === 'app-overview' ? 'scoping' : 'details';
  const selectedTab = endpointTabs[endpointId] || defaultTab;

  const setTab = (tab: EndpointTab) => setEndpointTab(endpointId, tab);

  return {
    selectedTab,
    setTab,
  };
};

export const useWebPageTabs = (webPageId: string) => {
  const webPageTabs = useLayoutStore((state) => state.webPageTabs);
  const setWebPageTab = useLayoutStore((state) => state.setWebPageTab);

  const defaultTab: WebPageTab = 'details';
  const selectedTab = webPageTabs[webPageId] || defaultTab;

  const setTab = (tab: WebPageTab) => setWebPageTab(webPageId, tab);

  return {
    selectedTab,
    setTab,
  };
};

export const useChatTabs = () => {
  const {
    chatTabs,
    activeChatTabId,
    setChatTabs,
    setActiveChatTabId,
    addChatTab,
    removeChatTab,
    updateChatTab,
    clearChatTabs,
  } = useLayoutStore(
    useShallow((state) => ({
      chatTabs: state.chatTabs,
      activeChatTabId: state.activeChatTabId,
      setChatTabs: state.setChatTabs,
      setActiveChatTabId: state.setActiveChatTabId,
      addChatTab: state.addChatTab,
      removeChatTab: state.removeChatTab,
      updateChatTab: state.updateChatTab,
      clearChatTabs: state.clearChatTabs,
    })),
  );

  const activeChatTab = useMemo(
    () => chatTabs.find((tab) => tab.tabId === activeChatTabId),
    [chatTabs, activeChatTabId],
  );

  return {
    chatTabs,
    activeChatTabId,
    activeChatTab,
    setChatTabs,
    setActiveChatTabId,
    addChatTab,
    removeChatTab,
    updateChatTab,
    clearChatTabs,
  };
};

export const useTabGroups = () => {
  const {
    tabGroups,
    tabGroupingSettings,
    createTabGroup,
    deleteTabGroup,
    updateTabGroup,
    addTabToGroup,
    removeTabFromGroup,
    toggleGroupCollapse,
    reorderTabsInGroup,
    reorderTabGroups,
    setTabGroupingSettings,
  } = useLayoutStore(
    useShallow((state) => ({
      tabGroups: state.tabGroups,
      tabGroupingSettings: state.tabGroupingSettings,
      createTabGroup: state.createTabGroup,
      deleteTabGroup: state.deleteTabGroup,
      updateTabGroup: state.updateTabGroup,
      addTabToGroup: state.addTabToGroup,
      removeTabFromGroup: state.removeTabFromGroup,
      toggleGroupCollapse: state.toggleGroupCollapse,
      reorderTabsInGroup: state.reorderTabsInGroup,
      reorderTabGroups: state.reorderTabGroups,
      setTabGroupingSettings: state.setTabGroupingSettings,
    })),
  );

  const getGroupByTabId = (tabId: string) => {
    return tabGroups.find((group) => group.tabIds.includes(tabId));
  };

  const getTabsInGroup = (groupId: string, allTabs: Tab[]) => {
    const group = tabGroups.find((g) => g.id === groupId);

    if (!group) return [];

    return group.tabIds
      .map((tabId) => allTabs.find((tab) => tab.id === tabId))
      .filter((tab): tab is Tab => tab !== undefined);
  };

  const getUngroupedTabs = (allTabs: Tab[]) => {
    return allTabs.filter((tab) => !tab.groupId);
  };

  return {
    tabGroups,
    tabGroupingSettings,
    createTabGroup,
    deleteTabGroup,
    updateTabGroup,
    addTabToGroup,
    removeTabFromGroup,
    toggleGroupCollapse,
    reorderTabsInGroup,
    reorderTabGroups,
    setTabGroupingSettings,
    getGroupByTabId,
    getTabsInGroup,
    getUngroupedTabs,
  };
};
